import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [error, setError] = useState("");

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.access_token) localStorage.setItem("access_token", data.access_token);
      setUser({ id: data.id, email: data.email, name: data.name, role: data.role, permissions: data.permissions || {}, view_prices: !!data.view_prices });
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.warn("Logout request failed (proceeding to clear local session):", e?.message); }
    localStorage.removeItem("access_token");
    setUser(false);
  }, []);

  const value = useMemo(() => {
    const isSuper = user?.role === "super_admin";
    const canRead = (m) => isSuper || ["read", "write"].includes(user?.permissions?.[m]);
    const canWrite = (m) => isSuper || user?.permissions?.[m] === "write";
    const canSeePrices = isSuper || !!user?.view_prices;
    return { user, login, logout, error, setError, fetchMe, isSuper, canRead, canWrite, canSeePrices };
  }, [user, login, logout, error, fetchMe]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
